import './crypto-polyfill';
import './iterator-helpers-polyfill';
import './promise-with-resolvers-polyfill';
import {
    encodeQueryString,
    removeQueryParamFromURL,
    BASE_ROOT,
} from '@ringcentral/rwc-utils/src/url';
import { isApplication, isMobile } from '@ringcentral/rwc-utils/src/client-detection';
import { getAnalyticsInstance } from '@ringcentral/rwc-utils/src/analytics/analytics-provider';
import {
    isAppliationExists,
    isForceDownload,
    tryLaunchApplication,
    isCurrentClientSupported,
    shouldSkipLauncher,
} from '@low';

import { makeOverridableFunction } from '@ringcentral/rwc-utils/src/override-api';
import { getBrandDomain } from '@ringcentral/rwc-branding/src/utils';
import {
    NO_INSTALL_AND_CLIENT_NOT_SUPPORT,
    IS_RCV_OR_CURRENT_CLIENT_SUPPORT,
} from '@ringcentral/rwc-bridge';
import { METADATA } from '@ringcentral/rwc-utils/src/web-client-info';

getAnalyticsInstance(METADATA.version);
const entry = {
    ONCONF: BASE_ROOT + 'conf/on/',
    WELCOME: BASE_ROOT + 'welcome',
    WELCOME_JOIN: BASE_ROOT + 'welcome/join/',
    JOIN: BASE_ROOT + 'join/',
    BRIDGE: BASE_ROOT + 'bridge/',
    DOWNLOAD_E2EE: BASE_ROOT + 'download-e2ee',
    DOWNLOAD: BASE_ROOT + 'download',
    UNSUPPORTED: BASE_ROOT + 'unsupported',
    LAUNCHER: BASE_ROOT + 'launcher',
    TERMINATED: BASE_ROOT + 'terminated',
    WAITING: BASE_ROOT + 'waiting',
    SETTINGS: BASE_ROOT + 'settings',
    PUBLIC_RECORDING: BASE_ROOT + 'recordings/',
};

const detectEntryPoint = () => {
    for (const key in entry) {
        // indexOf because of IE
        if (location.pathname.indexOf(entry[key]) === 0) {
            return entry[key];
        }
    }

    return entry.WELCOME;
};

const getMeetingId = (entryPoint, pathname) => {
    switch (entryPoint) {
        case entry.JOIN:
        case entry.WELCOME_JOIN:
        case entry.ONCONF:
            return pathname.slice(entryPoint.length);
        case entry.UNSUPPORTED:
        case entry.LAUNCHER:
        case entry.DOWNLOAD_E2EE:
        case entry.DOWNLOAD:
            // +1 because of /
            return pathname.slice(entryPoint.length + 1);
        default:
            return null;
    }
};

const entryPoint = detectEntryPoint();
const pathname = location.pathname;
const search = location.search || '';

export const meetingId = getMeetingId(entryPoint, pathname);
export const isDownloadE2ee = entryPoint === entry.DOWNLOAD_E2EE;

const getPageUri = page => {
    if (!meetingId) {
        return page + search;
    }

    return page.replace(/\/?$/, `/${meetingId}`, '') + search;
};

const isInQuery = key => search.match(new RegExp(`[?&]${key}=`));

const withRedirectUriWhenAuthFromGlip = uri =>
    isInQuery('code') && !isInQuery('auth_code_redirect_uri')
        ? `${uri}&auth_code_redirect_uri=${encodeURIComponent(
              removeQueryParamFromURL('code', location.href)
          )}`
        : uri;

const goToPage = page => () => {
    location.href = withRedirectUriWhenAuthFromGlip(getPageUri(page));
};

//fabric with meeting id parameter
export const gotoDownload = goToPage(entry.DOWNLOAD);
export const gotoUnsupported = goToPage(entry.UNSUPPORTED);
export const gotoLauncher = goToPage(entry.LAUNCHER);
export const gotoOnconf = goToPage(entry.ONCONF);
export const gotoWelcome = goToPage(entry.WELCOME_JOIN);

const noInstallAndClientNotSupport = makeOverridableFunction(
    gotoDownload,
    NO_INSTALL_AND_CLIENT_NOT_SUPPORT
);

const hasNoInstalledClientCb = () => {
    if (isCurrentClientSupported() && (meetingId || !isForceDownload())) {
        if (meetingId) {
            gotoOnconf();
        } else {
            gotoWelcome();
        }
    } else {
        noInstallAndClientNotSupport();
    }
};

const getDesktopApplicationUri = () =>
    meetingId ? getPageUri(entry.ONCONF) : getPageUri(entry.WELCOME);

const getMobileApplicationUri = () => {
    const mobileAppUrlSchemeBaseUri = 'video';
    const mobileAppUrlSchemeMeetingUri = 'video/join';
    if (!meetingId) {
        return mobileAppUrlSchemeBaseUri;
    }

    return (
        mobileAppUrlSchemeMeetingUri +
        '?' +
        encodeQueryString({
            link: `${location.protocol}//${getBrandDomain()}/join/${meetingId}${search}`,
        })
    );
};

export const reopenApplication = () => {
    const uri = !isMobile ? getDesktopApplicationUri() : getMobileApplicationUri();
    tryLaunchApplication(uri);
};

let guard = Promise.resolve();

const preventRenderingPageWhenRedirecting = () => {
    guard = Promise.reject('Prevent render due to redirect or application launch');
};

const tryJoinFromBrowserOrLaunchApp = uri =>
    isCurrentClientSupported() && shouldSkipLauncher()
        ? gotoOnconf()
        : tryLaunchApplication(
              uri,
              gotoLauncher,
              hasNoInstalledClientCb,
              hasNoInstalledClientCb,
              isCurrentClientSupported() && meetingId ? gotoOnconf : undefined
          );

const isRCVOrCurrentClientSupport = makeOverridableFunction(
    () => true,
    IS_RCV_OR_CURRENT_CLIENT_SUPPORT
);

export default async function () {
    const entryPoint = detectEntryPoint();

    switch (entryPoint) {
        case entry.JOIN:
            preventRenderingPageWhenRedirecting();
            if (!isAppliationExists() || isApplication()) {
                if (!isCurrentClientSupported()) {
                    gotoUnsupported();
                } else {
                    gotoOnconf();
                }
            } else {
                if (isMobile) {
                    if (isCurrentClientSupported()) {
                        if (shouldSkipLauncher()) {
                            gotoOnconf();
                        } else {
                            gotoLauncher();
                        }
                    } else {
                        gotoDownload();
                    }
                } else {
                    tryJoinFromBrowserOrLaunchApp(getPageUri(entryPoint));
                }
            }

            break;
        case entry.UNSUPPORTED:
            if (isAppliationExists() && !isApplication() && isRCVOrCurrentClientSupport()) {
                const uri = meetingId ? entry.ONCONF : entry.WELCOME_JOIN;
                preventRenderingPageWhenRedirecting();
                tryJoinFromBrowserOrLaunchApp(getPageUri(uri));
            } else if (isCurrentClientSupported()) {
                if (meetingId) {
                    gotoOnconf();
                } else {
                    gotoWelcome();
                }
            }

            break;
        case entry.DOWNLOAD:
        case entry.LAUNCHER:
            if (!isAppliationExists()) {
                preventRenderingPageWhenRedirecting();
                if (isCurrentClientSupported()) {
                    gotoWelcome();
                } else {
                    gotoUnsupported();
                }
            }

            break;
        case entry.TERMINATED:
        case entry.WAITING:
            if (!isCurrentClientSupported()) {
                preventRenderingPageWhenRedirecting();
                if (isApplication() || !isAppliationExists()) {
                    gotoUnsupported();
                } else {
                    const successUri =
                        entry.WAITING && isInQuery('url') ? entry.WAITING : entry.WELCOME;
                    tryLaunchApplication(
                        getPageUri(successUri),
                        gotoLauncher,
                        gotoDownload,
                        gotoDownload
                    );
                }
            }

            break;
        case entry.PUBLIC_RECORDING:
            break;
        case entry.WELCOME:
        case entry.ONCONF:
        case entry.BRIDGE:
        case entry.SETTINGS:
        default:
            if (!isCurrentClientSupported()) {
                preventRenderingPageWhenRedirecting();
                if (isAppliationExists() && !isApplication()) {
                    tryLaunchApplication(
                        getPageUri(pathname),
                        gotoLauncher,
                        gotoDownload,
                        gotoDownload
                    );
                } else {
                    gotoUnsupported();
                }
            }

            break;
    }

    return guard;
}

const getDesktopApplicationRecordingUri = url => `v/recording?url=${encodeURIComponent(url)}`;
const getMobileApplicationRecordingUri = url => `video/recording?url=${encodeURIComponent(url)}`;

/**
 * Open recording via Jupiter or RingCentral mobile app
 * Samples:
 * - Jupiter: rcvdt://v/recording?url=https://v.ringcentral.com/recordings/{id}
 * - mThor: glip://video/recording?url=https://v.ringcentral.com/recordings/{id}
 */
export const openRecordingViaApplication = url => {
    const uri = !isMobile
        ? getDesktopApplicationRecordingUri(url)
        : getMobileApplicationRecordingUri(url);
    tryLaunchApplication(uri);
};
